import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

export interface ColorSwatchProps {
  borderColor?: string;
  darkerBorder?: boolean;
  dataTestAttrs?: DataTestAttrs;
  fillColor: string;
  onClick?: () => void;
  small?: boolean;
}

/**
 * Renders a 25px square swatch with the given color.
 *
 * @param fillColor Fill color of the swatch.
 * @param darkerBorder Whether to display a darker border, overriding hover state if it exists.
 * @param dataTestAttrs Data test attributes for browser testing.
 * @param onClick Callback to execute when the swatch is clicked. If passed, shows a darker border on hover.
 * @param small Whether to render a 16px square swatch instead of the default 25px square.
 * @constructor
 *
 * @example
 * function Example() {
 *   const [selected, setSelected] = useState(false);
 *
 *   return (
 *     <ColorSwatch fillColor={Color.RED_400} darkerBorder={selected} onClick={() => setSelected(!selected)} />
 *   );
 * }
 */
export function ColorSwatch({
  fillColor,
  darkerBorder = false,
  dataTestAttrs,
  onClick,
  small = false,
}: ColorSwatchProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={classnames(css(styles.colorSwatch), {
        [css(styles.smallColorSwatch)]: small,
        [css(styles.pointerCursor)]: onClick != null,
        [css(styles.colorSwatchDefault)]: !isHovered && !darkerBorder,
        [css(styles.colorSwatchHover)]: isHovered,
        [css(styles.colorSwatchSelected)]: !isHovered && darkerBorder,
      })}
      onClick={onClick}
      onMouseEnter={() => {
        if (onClick != null) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (onClick != null) {
          setIsHovered(false);
        }
      }}
      style={{backgroundColor: fillColor, border: '2px solid'}}
      {...getTestProps(dataTestAttrs)}
    />
  );
}

const styles = StyleSheet.create({
  colorSwatch: {
    height: '25px',
    width: '25px',
    padding: '0',
  },
  colorSwatchDefault: {
    borderWidth: 'thin',
    borderColor: Color.GRAY_400,
  },
  colorSwatchHover: {
    borderWidth: 'default',
    borderColor: Color.BLUE_400,
  },
  colorSwatchSelected: {
    borderWidth: 'default',
    borderColor: Color.BLUE_500,
  },
  pointerCursor: {
    cursor: 'pointer',
  },
  smallColorSwatch: {
    width: '16px',
    height: '16px',
  },
});
